import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';

import group from 'src/assets/images/organisation.svg';
import person from 'src/assets/images/person.svg';
import { useGetEntity } from 'src/features/entity/api/useGetEntity';
import { entityMessages } from 'src/features/entity/utils/messages';
import { ENTITY_TYPE } from 'src/features/entity/types';
import { PATH } from 'src/features/navigation/utils/constants';
import { Button } from 'src/registry/ui/Button';

import { generalMessages } from 'src/registry/utils/messages';
import { useTitleStore } from 'src/stores/useTitleStore';
import { ICON_MAP } from 'src/utils/constants';
import { SectionBlockSubtitle, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import { commonMessages } from 'src/utils/messages';
import EntityDashboardTables from 'src/features/entity/components/EntityDashboardTables';
import DeleteEntity from 'src/features/entity/components/forms/DeleteEntity';
import UpdateEntityFormModal from 'src/features/entity/components/forms/UpdateEntityFormModal';
import GeneralBlockAttribute from 'src/registry/components/blocks/GeneralBlockAttribute';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { useParking } from 'src/providers/ParkingProvider';

const EntityDashboard = () => {
    const { formatMessage } = useIntl();
    const { setTitle, removeTitle } = useTitleStore();
    const manageEntityPermission = useHasPermissions([PERMISSION.MANAGE_ENTITIES]);
    const { parkingId } = useParking();
    const { type = '', id = '' } = useParams();

    const { data: entityData, isLoading } = useGetEntity({
        parkingId,
        id,
        config: { enabled: !!id },
    });

    const pageTitle =
        type === ENTITY_TYPE.PERSON
            ? formatMessage(entityMessages.personDashboard)
            : formatMessage(entityMessages.groupDashboard);
    React.useEffect(() => {
        setTitle(pageTitle);

        return removeTitle;
    }, [formatMessage, removeTitle, setTitle, pageTitle]);

    const dashboardImage = type === ENTITY_TYPE.PERSON ? person : group;

    if (!entityData && isLoading) return null;

    return (
        <>
            <Button asChild className="-mt-6 place-self-start p-0" variant="link">
                <Link to={`/${PATH.ENTITIES}/${type}`}>
                    <FontAwesomeIcon icon={ICON_MAP.BACK} className="opacity-50" />
                    {formatMessage(entityMessages.backToCRM)}
                </Link>
            </Button>

            <div className="flex flex-row items-end justify-between p-0">
                <span className="flex flex-col items-start">
                    <SectionBlockTitle className="mr-2">{entityData?.name}</SectionBlockTitle>
                    <SectionBlockSubtitle>{entityData?.identifier}</SectionBlockSubtitle>
                </span>
                {entityData && manageEntityPermission && (
                    <div className="flex flex-row gap-2">
                        <UpdateEntityFormModal selectedEntity={entityData}>
                            <Button variant="outline">{formatMessage(commonMessages.edit)}</Button>
                        </UpdateEntityFormModal>

                        <DeleteEntity selectedEntity={entityData}>
                            <Button variant="destructiveLight">{formatMessage(generalMessages.delete)}</Button>
                        </DeleteEntity>
                    </div>
                )}
            </div>

            <div className="flex gap-9">
                <GeneralBlock className="flex-1 items-center justify-center">
                    <img src={dashboardImage} alt={type} />
                </GeneralBlock>
                <GeneralBlock className="grid flex-3 grid-cols-3">
                    <div className="flex flex-col gap-4">
                        <GeneralBlockAttribute
                            label={formatMessage(entityMessages.phone)}
                            value={entityData?.phone}
                        />
                        <GeneralBlockAttribute
                            label={formatMessage(entityMessages.email)}
                            value={entityData?.email}
                        />
                    </div>
                    <div className="flex flex-col gap-4">
                        <GeneralBlockAttribute
                            label={formatMessage(entityMessages.description)}
                            value={entityData?.description}
                        />
                    </div>
                </GeneralBlock>
            </div>
            <EntityDashboardTables entityData={entityData} />
        </>
    );
};

export default EntityDashboard;
